import { Injectable, inject } from '@angular/core';
import { ActionState, LOCAL_STORAGE_VOUCHER_KEY } from '@mkp/shared/models';
import { isMessageError, MessageError } from '@mkp/shared/data-access';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { SendVerificationEmailApi } from '@mkp/user/data-access';
import { onboardingActions } from '@mkp/core/state/actions';
import { selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { UserDisplayType } from '@mkp/user/models';

@Injectable()
export class SettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationsService = inject(SnackbarService);
  private readonly sendVerificationEmailApi = inject(SendVerificationEmailApi);
  private store = inject(Store);

  resendVerificationEmail$ = createEffect(
    () =>
      this.actions$.pipe(ofType(onboardingActions.resendEmailVerificationStart)).pipe(
        switchMap(({ userId }) =>
          this.sendVerificationEmailApi.sendVerificationEmail(userId).pipe(
            tap(() => this.notificationsService.show('ONBOARDING.VERIFY.RESEND_EMAIL.SUCCESS')),
            map(() => onboardingActions.resendEmailVerificationSuccess()),
            catchError((error: unknown) => {
              console.error(error);
              this.notificationsService.show('ONBOARDING.VERIFY.RESEND_EMAIL.FAILURE', {
                state: ActionState.Error,
              });

              return (
                isSettingsError(error) &&
                of(onboardingActions.resendEmailVerificationFailure({ error }))
              );
            })
          )
        )
      ),
    { useEffectsErrorHandler: false }
  );

  saveVoucher$ = createEffect(
    () =>
      this.actions$.pipe(ofType(routerNavigatedAction)).pipe(
        filter(({ payload }) => payload.event.url.includes('onboarding')),
        map(({ payload }) => payload.routerState['queryParams'][LOCAL_STORAGE_VOUCHER_KEY]),
        filter(Boolean),
        concatLatestFrom(() => this.store.select(selectLoggedInUser)),
        tap(([voucher, currentUser]) => {
          if (
            !currentUser ||
            currentUser.settings.displayType !== UserDisplayType.CustomerService
          ) {
            this.saveVoucherToLocalStorage(voucher as string);
          }
        })
      ),
    { dispatch: false, useEffectsErrorHandler: false }
  );

  saveVoucherToLocalStorage(voucher: string) {
    localStorage.setItem(LOCAL_STORAGE_VOUCHER_KEY, voucher);
  }
}

export const getNotificationMessage = (page: string | null): string => page?.toUpperCase() ?? '';

const isSettingsError = (error: unknown): error is MessageError =>
  isMessageError(error, 'SettingsEffects');
